import type {
  PublicConversation,
  PublicMessage,
  PublicGroupInvite,
  GroupInvitePreview,
  PublicIdentity,
  FormerMember,
} from '@adieuu/shared';
import type { RecipientKeys, MemberSettingsMap } from '../../services/conversationCryptoService';
import type { SignatureRecheckSummary } from '../../services/signatureRecheck';
import type { LocalPendingSend } from '../../services/pendingSends';

export interface DecryptedConversation extends PublicConversation {
  decryptedName?: string;
  decryptedMemberSettings?: MemberSettingsMap;
  unreadCount: number;
  /** True when the encrypted read pointer is behind lastMessageId (set on load, cleared on open). */
  hasUnread: boolean;
}

export interface DisplayMessage extends PublicMessage {
  decryptedContent?: string;
  signatureVerified?: boolean;
  /**
   * Signature preimage version that verified (`1` = ciphertext-only, `2` =
   * conversation-bound). Undefined when verification failed or the message is
   * unsigned legacy history.
   */
  signatureVersion?: 1 | 2;
  /** Hybrid signature failed only because the sender's ML-DSA key was not cached yet. */
  signaturePendingPqKey?: boolean;
  /**
   * Epoch ms until which a pending row renders a neutral checking state while
   * a key fetch is in flight. Past this instant the row renders as unverified.
   */
  signatureCheckingUntil?: number;
  decryptionError?: string;
  forwardSecrecy?: boolean;
  /**
   * Forward secrecy was requested at send time but one or more recipient
   * devices fell back to static key wrapping (no pre-keys available or SPK
   * verification failed). Only set on locally sent messages.
   */
  fsDowngraded?: boolean;
  /** Local row for a send the server has not confirmed yet (never in the buffer). */
  pendingSend?: LocalPendingSend;
}

/** One prior ciphertext version after E2E edit (decrypted in the client for history UI). */
export interface MessageEditHistoryEntry {
  replacedAt: string;
  plaintext?: string;
  decryptionError?: string;
}

/** One decrypted page of a conversation thread. */
export interface ConversationThreadPage {
  root: DisplayMessage;
  /** Oldest-first. */
  replies: DisplayMessage[];
  cursor: string | null;
}

export interface SendMessageErrorResult {
  errorCode: string;
}

export interface ConversationMessagesState {
  messages: DisplayMessage[];
  /** Next `cursor` with `direction=older` (from API `cursor`). */
  olderCursor: string | null;
  /**
   * Newest message id still in the buffer (`messages[0]`); must match after merge/trim so
   * `after` pagination is never stale when {@link trimMessagesBuffer} evicts toward the present.
   */
  newerPaginationAfterId: string | null;
  /** More messages exist toward the present than are currently in the buffer (or were evicted by trim). */
  hasNewerPages: boolean;
  loading: boolean;
  /**
   * True while a reconcile refresh (window focus / reconnect) is in flight.
   * Drives an overlay spinner that must not change message-list height.
   */
  loadingOverlay?: boolean;
  /**
   * The last paged request (older/initial) returned only non-visible messages; show a CTA
   * so the user can fetch the next page on demand (avoids auto chain-fetching unreadable history).
   */
  showManualLoadOlder: boolean;
  /** Same for paging toward the present (e.g. gap of undecryptable messages). */
  showManualLoadNewer: boolean;
}

export const EMPTY_MESSAGES: DisplayMessage[] = [];
export const EMPTY_MEMBER_SETTINGS: MemberSettingsMap = {};

export type FetchConversationMessagesFn = (
  conversationId: string,
  paginationCursor?: string,
  silent?: boolean,
  mergeLatest?: boolean,
  direction?: 'older' | 'newer',
  options?: { overlay?: boolean },
) => Promise<void>;

export interface ConversationsContextValue {
  conversations: DecryptedConversation[];
  activeConversationId: string | null;
  activeMessages: DisplayMessage[];
  activeMessagesOlderCursor: string | null;
  activeMessagesHasNewerPages: boolean;
  invites: PublicGroupInvite[];
  participantProfiles: Record<string, PublicIdentity>;
  memberSettings: MemberSettingsMap;

  loading: boolean;
  messagesLoading: boolean;
  /** Overlay spinner for focus/reconnect refresh; does not offset the message list. */
  messagesLoadingOverlay: boolean;
  sending: boolean;

  setActiveConversation: (id: string | null) => void;
  setIsAtBottom: (value: boolean) => void;
  /** Merges `GET /conversations/:id` (e.g. `messageCount`) into local state. */
  fetchConversationById: (conversationId: string) => Promise<void>;
  markConversationRead: (conversationId: string, readUpToMessageId?: string) => void;

  createDM: (
    participantId: string,
    options?: { forceNew?: boolean; topic?: string }
  ) => Promise<PublicConversation | null>;
  createGroup: (
    participantIds: string[],
    conversationTopicOrName?: string
  ) => Promise<PublicConversation | null>;

  sendTextMessage: (
    conversationId: string,
    plaintext: string,
    options?: {
      expiresInSeconds?: number;
      useForwardSecrecy?: boolean;
      replyToMessageId?: string;
      e2eMediaIds?: string[];
      mentionedIdentityIds?: string[];
      /** Thread reply: any message in the thread (the server resolves the root). */
      threadRootId?: string;
      /** With `threadRootId`, also show the reply in the conversation stream. */
      alsoSendToConversation?: boolean;
      /** When true, do not merge the sent message into local message state (caller will refresh, e.g. jump to latest). */
      skipMessageStateUpdate?: boolean;
      /** When true, do not toggle global `sending` (e.g. background media outbox). */
      suppressGlobalSending?: boolean;
      /** Aborts in-flight send (e.g. media outbox cancel). */
      signal?: AbortSignal;
      /** Caller-chosen id (optimistic sends match the server copy on it). */
      clientMessageId?: string;
    }
  ) => Promise<PublicMessage | SendMessageErrorResult | null>;

  /** Replace text/attachments for an existing message (E2E); max 3 edits per message server-side. */
  editTextMessage: (
    conversationId: string,
    messageId: string,
    plaintext: string,
    options?: {
      useForwardSecrecy?: boolean;
      e2eMediaIds?: string[];
      signal?: AbortSignal;
      /** Original message clientMessageId (bound into the v2 signature). */
      clientMessageId?: string;
    }
  ) => Promise<PublicMessage | SendMessageErrorResult | null>;

  /** Fetches and decrypts `encryptedRevisionHistory` for a message (read-only, no pre-key side effects). */
  loadMessageEditHistory: (conversationId: string, message: DisplayMessage) => Promise<MessageEditHistoryEntry[] | null>;

  /**
   * True when the given conversation's buffer is at the live tail (same heuristic as the composer adapter).
   */
  computeAtLiveTail: (conversationId: string) => boolean;
  loadOlder: () => Promise<void>;
  loadNewer: () => Promise<void>;
  activeShowManualLoadOlder: boolean;
  activeShowManualLoadNewer: boolean;
  jumpToLatestMessages: (conversationId: string) => Promise<void>;
  fetchMessagesAround: (
    conversationId: string,
    centerMessageId: string,
    options?: {
      before?: number;
      after?: number;
      /** Do not replace conversation buffer (e.g. gathering report evidence keys). */
      skipStateUpdate?: boolean;
      /** With skipStateUpdate: suppress failure toast (caller shows error). */
      silent?: boolean;
    }
  ) => Promise<DisplayMessage[] | null>;
  loadPinnedMessagesPage: (
    conversationId: string,
    cursor?: string | null
  ) => Promise<{ messages: DisplayMessage[]; nextCursor: string | null } | null>;
  /** Thread root + one page of replies, decrypted without touching the conversation buffer. */
  loadThreadPage: (
    conversationId: string,
    messageId: string,
    options?: { before?: string; limit?: number }
  ) => Promise<import('../threads/useMessageThread').ThreadFetchResult<ConversationThreadPage>>;
  /** One message, decrypted without touching the conversation buffer. */
  loadThreadMessage: (conversationId: string, messageId: string) => Promise<DisplayMessage | null>;
  replyParentHydrationMap: Record<string, DisplayMessage>;
  ensureReplyParentHydration: (conversationId: string, parentMessageId: string) => Promise<void>;
  deleteMessage: (
    conversationId: string,
    messageId: string,
    forEveryone: boolean
  ) => Promise<boolean>;

  addMember: (
    conversationId: string,
    identityId: string
  ) => Promise<{ ok: boolean; errorCode?: string; message?: string }>;
  removeMember: (conversationId: string, identityId: string) => Promise<boolean>;
  leaveGroup: (
    conversationId: string,
    options?: { transferAdminTo?: string; transferStrategy?: 'oldest' | 'most_active' }
  ) => Promise<boolean>;
  renameGroup: (conversationId: string, newName: string) => Promise<boolean>;
  updateMemberSettings: (conversationId: string, settings: MemberSettingsMap) => Promise<boolean>;
  updateGifsDisabled: (conversationId: string, gifsDisabled: boolean) => Promise<boolean>;
  updateGifContentFilter: (
    conversationId: string,
    gifContentFilter: import('@adieuu/shared').GifContentFilter
  ) => Promise<boolean>;
  updateCustomEmojisDisabled: (
    conversationId: string,
    customEmojisDisabled: boolean
  ) => Promise<boolean>;
  updateMessageSearchCachePolicy: (
    conversationId: string,
    disallowPersistentMessageSearchCache: boolean
  ) => Promise<boolean>;
  updateAllowSkipModeration: (
    conversationId: string,
    allowSkipModeration: boolean
  ) => Promise<boolean>;
  updateCallSettings: (
    conversationId: string,
    settings: { audioCallsDisabled?: boolean; videoCallsDisabled?: boolean; screenshareDisabled?: boolean }
  ) => Promise<boolean>;
  pinMessage: (conversationId: string, messageId: string) => Promise<boolean>;
  unpinMessage: (conversationId: string, messageId: string) => Promise<boolean>;
  promoteToAdmin: (conversationId: string, identityId: string) => Promise<boolean>;
  terminateGroup: (conversationId: string) => Promise<boolean>;

  acceptInvite: (inviteId: string) => Promise<boolean>;
  declineInvite: (inviteId: string) => Promise<boolean>;
  getInvitePreview: (inviteId: string) => Promise<GroupInvitePreview | null>;

  getFormerMembers: (conversationId: string) => Promise<FormerMember[]>;

  pendingInvitesRefreshSignal: { conversationId: string; nonce: number } | null;
  listPendingGroupInvites: (conversationId: string) => Promise<PublicGroupInvite[]>;
  revokeGroupInvite: (conversationId: string, inviteId: string) => Promise<boolean>;
  prefetchParticipantProfiles: (identityIds: string[]) => Promise<Record<string, PublicIdentity>>;

  fetchRecipientKeys: (
    conversationId: string,
    participantIds: string[],
    useForwardSecrecy?: boolean,
    signal?: AbortSignal
  ) => Promise<RecipientKeys[]>;

  /** Re-wrap the conversation metadata key after accepting a new device. */
  syncConversationMetadata: (conversationId: string) => Promise<void>;

  getSessionKeysForMessages: (messageIds: string[]) => Promise<Record<string, string>>;

  /**
   * Record signing keys fetched elsewhere (e.g. Device Signatures). Fills empty
   * cache entries only; returns true when an ML-DSA key was newly cached.
   */
  notePeerSigningKeys: (
    identityId: string,
    signingPublicKey?: string,
    pqSigningPublicKey?: string
  ) => boolean;
  /** Refetch missing sender ML-DSA keys and recheck rows pending on them. */
  recheckPendingSignatures: (
    conversationId: string,
    options?: { force?: boolean }
  ) => Promise<SignatureRecheckSummary>;
  /** Set when rows already shown as unverified verified after a key refresh (active conversation). */
  signatureRefreshNotice: SignatureRefreshNotice | null;
  dismissSignatureRefreshNotice: () => void;

  refresh: () => Promise<void>;
}

export interface SignatureRefreshNotice {
  conversationId: string;
  healedCount: number;
  senderIdentityIds: string[];
}
